import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { CancellationFlowOrigin } from 'src/app/_enums/cancellation/cancellation-flow-origin.enum';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { APIErrorResponse } from 'src/app/_types/api-responses.types';
import {
  InsuranceFlowDetailsResponse,
  InsuranceFlowOption,
} from 'src/app/_types/insurance-flow.types';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { FlowSelectComponent } from 'src/app/shared/components/flow-select/flow-select.component';

/**
 * Opens a cancellation for the policy being viewed.
 *
 * The policy itself is not touched: it reaches « Résiliée » only once the
 * replacement policy is active, which is what the cancellation drives. The
 * replacement is optional, but not implicitly — the agent either picks one or
 * says there is none, because forgetting to name one is the likelier mistake.
 *
 * The picker is restricted to the affaire being cancelled: a replacement is
 * created as a child of the policy's flow and so shares its display id, and the
 * server rejects anything else.
 */
@Component({
  selector: 'app-cancel-policy-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ActionModalComponent,
    FlowSelectComponent,
  ],
  templateUrl: './cancel-policy-modal.component.html',
  styleUrls: ['./cancel-policy-modal.component.css'],
})
export class CancelPolicyModalComponent implements OnDestroy {
  @Input()
  flow!: InsuranceFlowDetailsResponse | null;

  @Output()
  cancellationCreated = new EventEmitter<void>();

  @ViewChild('actionModal')
  modal!: ActionModalComponent;

  protected readonly noReplacement = new FormControl(false, {
    nonNullable: true,
  });

  protected readonly replacement = new FormControl<InsuranceFlowOption | null>(
    null
  );

  protected submitted = false;

  /**
   * The picker searches as soon as it exists, and content projected into
   * `app-action-modal` is created with *this* view — so it is built only while
   * the modal is up, or every stepper would search on page load.
   */
  protected modalOpen = false;

  private subscription?: Subscription;

  constructor(
    private readonly cancellationService: CancellationService,
    private readonly toastr: ToastrService
  ) {
    this.noReplacement.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((noReplacement) => {
        if (noReplacement) {
          this.replacement.reset(null);
          this.replacement.disable();
        } else {
          this.replacement.enable();
        }
      });
  }

  openModal(): void {
    this.noReplacement.setValue(false);
    this.replacement.reset(null);
    this.submitted = false;
    this.modalOpen = true;
    this.modal.openModal();
  }

  protected onClosed(): void {
    this.modalOpen = false;
  }

  /** Nothing to confirm until the agent has either picked a replacement or ruled one out. */
  protected get submitDisabled(): boolean {
    return !this.noReplacement.value && !this.replacement.value;
  }

  protected confirm(): void {
    if (this.submitted || !this.flow) return;
    this.submitted = true;
    this.subscription = this.cancellationService
      .createCancellationFlow({
        origin: CancellationFlowOrigin.POLICY,
        baseInsuranceFlowId: this.flow.id,
        replacementInsuranceFlowId: this.replacement.value?.id ?? null,
      })
      .subscribe({
        next: (res) => {
          this.toastr.success(
            $localize`Demande de résiliation ${res.data.displayId} créée`
          );
          this.modal.closeModal();
          this.cancellationCreated.emit();
          this.submitted = false;
        },
        error: (err: HttpErrorResponse) => {
          const apiError = err?.error as APIErrorResponse;
          this.toastr.error(
            apiError?.error?.clientErrorMessageInFrench ??
              apiError?.error?.clientErrorMessage ??
              $localize`Une erreur est survenue. Veuillez réessayer`
          );
          this.submitted = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
